// frontend/src/api.js

import axios from 'axios'

// 動態取得 API 基礎網址
function getApiBaseUrl() {
  // 如果有設定環境變數，優先使用
  if (process.env.VUE_APP_API_BASE_URL) {
    return process.env.VUE_APP_API_BASE_URL
  }
  
  // 根據目前網址動態決定 API 位置
  const hostname = window.location.hostname
  const protocol = window.location.protocol
  
  // 本機開發環境
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:8000/api`
  }
  
  // 生產環境或其他 IP（使用相同主機的 8000 port）
  return `${protocol}//${hostname}:8000/api`
}

// 1. 建立一個 Axios instance，baseURL 動態決定
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: process.env.VUE_APP_API_TIMEOUT || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 匯出 getApiBaseUrl 供其他元件使用
export { getApiBaseUrl }

// 2. Request 攔截器 - 自動加入 token
api.interceptors.request.use(
  (config) => {
    // 從 localStorage 取得 token
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    console.log('API Request:', config.method.toUpperCase(), config.baseURL + config.url)
    return config
  },
  (error) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 3. Response 攔截器 - 統一錯誤處理
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.config.url, response.status)
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data)
    
    // 如果是 401 錯誤，清除 token 並導向登入頁
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('token_type')
      localStorage.removeItem('user_info')
      // 如果有 Vue Router 實例，導向登入頁
      if (window.$router) {
        window.$router.push('/login')
      }
    }   
    
    return Promise.reject(error)
  }
)

// 4. 註冊／登入相關
export function registerUser(payload) {
  return api.post('/auth/register', payload)
}

export function loginUser(payload) {
  return api.post('/auth/login', payload)
}

// 以有效 token 換新 token（頁面停留期間自動續期，避免中途被登出）
export function refreshToken() {
  return api.post('/auth/refresh', {})
}

// 5. 取得使用者個人資料
export function getProfile() {
  return api.get('/user/profile')
}

// 6. 活動相關 API
export function getActivities(params = {}) {
  return api.get('/activities', { params })
}

export function getActivityById(id) {
  return api.get(`/activities/${id}`)
}

export function createActivity(activityData) {
  return api.post('/activities', activityData)
}

export function registerActivity(activityId) {
  return api.post(`/activities/${activityId}/register`, {})
}

// 7. 取得使用者已報名的活動列表
export function getUserActivities() {
  return api.get('/user/activities')
}

// 7-1. 取得使用者學習歷程點數（5 大類別 + 惠蓀講座動態點數）
export function getMyPoints() {
  return api.get('/my-points')
}

// 7-2. 報到（簽到）
export function checkinActivity(activityId, account) {
  return api.post(`/activities/${activityId}/checkin`, { account })
}

// 7-3. 簽退
export function checkoutActivity(activityId, account) {
  return api.post(`/activities/${activityId}/checkout`, { account })
}

// 7-4. 現場加簽（未報名者，免密碼）→ 記錄報到時間、資格待審核
export function pendingCheckin(activityId, account) {
  return api.post(`/activities/${activityId}/pending-checkin`, { account })
}

// 7-5. 取得現場加簽（待審核）名單
export function getPendingSignups(activityId) {
  return api.get(`/activities/${activityId}/pending-signups`)
}

// 7-6. 核准現場加簽（加入正式報名）
export function approvePendingSignup(activityId, pendingId) {
  return api.post(`/activities/${activityId}/pending/${pendingId}/approve`, {})
}

// 7-7. 否決現場加簽（取消）
export function rejectPendingSignup(activityId, pendingId) {
  return api.post(`/activities/${activityId}/pending/${pendingId}/reject`, {})
}

// 7-8. 匯入前驗證名單（回傳查詢不到的學號/帳號）
export function validateImportNamelist(activityId, file) {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/activities/${activityId}/registrations/validate-import`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// 7-9. 匯入活動名單（CSV, multipart）；大量名單較久，給專屬較長 timeout
export function importRegistrations(activityId, file) {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/activities/${activityId}/registrations/import`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  })
}

// 活動公告（公開讀 / admin 寫）
export function getActivityAnnouncement() {
  return api.get('/settings/activity-announcement')
}
export function updateActivityAnnouncement(announcement) {
  return api.put('/settings/activity-announcement', { announcement })
}

// 活動刪除申請（owner/admin 送出）
export function requestActivityDeletion(activityId, reason) {
  return api.post(`/activities/${activityId}/delete-request`, { reason })
}

// 更換活動承辦人（僅 admin）
export function changeActivityOrganizer(activityId, userId) {
  return api.put(`/activities/${activityId}/organizer`, { user_id: userId })
}

// 活動刪除申請列表（admin）
export function getActivityDeleteRequests() {
  return api.get('/activity-delete-requests')
}

// 審核活動刪除申請（admin）
export function reviewActivityDeleteRequest(reqId, status, comment = '') {
  return api.post(`/activity-delete-requests/${reqId}/review`, { status, comment })
}

// 心得繳交相關 API
// 取得登入者的心得狀態（待繳交 / 已通過）
export function getMyReflections() {
  return api.get('/my-reflections')
}

// 上傳心得（PDF, multipart）
export function uploadReflection(activityId, file) {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/activities/${activityId}/reflection`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// 下載/檢視心得 PDF（帶 token，回傳 blob）
export function downloadReflectionFile(reflectionId) {
  return api.get(`/reflections/${reflectionId}/file`, { responseType: 'blob' })
}

// admin/TA：取得心得審核清單
export function getReflectionsForReview() {
  return api.get('/reflections/review')
}

// admin/TA：審核心得（approved / rejected）
export function reviewReflection(reflectionId, status, comment = '') {
  return api.post(`/reflections/${reflectionId}/review`, { status, comment })
}

// 8. 取消報名活動
export function cancelRegistration(activityId) {
  return api.delete(`/activities/${activityId}/register`)
}

// 9. 取得活動申請列表
export function getActivityApplications() {
  return api.get('/activity-applications')
}

// 10. 審核活動申請
export function reviewApplication(applicationId, reviewData) {
  return api.post(`/activity-applications/${applicationId}/review`, reviewData)
}

// 10-1. 取得自己的活動申請追蹤（含草稿）
export function getMyActivityApplications() {
  return api.get('/my-activity-applications')
}

// 10-2. 儲存活動申請草稿
export function saveActivityDraft(activityData) {
  return api.post('/activity-applications/draft', activityData)
}

// 10-3. 編輯自己的活動申請（草稿 / 被駁回）；submit=true 代表重新送審
export function updateActivityApplication(applicationId, payload) {
  return api.put(`/activity-applications/${applicationId}`, payload)
}

// 10-4. 取得活動管理可見活動（admin 全部 / dept 僅自己建立）
export function getManagedActivities() {
  return api.get('/managed-activities')
}

// 11. 取得活動報名列表
export function getActivityRegistrations(activityId) {
  return api.get(`/activities/${activityId}/registrations`)
}

// 12. 管理員刪除特定報名記錄
export function deleteRegistrationById(registrationId) {
  return api.delete(`/registrations/${registrationId}`)
}

// 13. 匯出 axios instance 供特殊需求使用
export default api

// 權限管理 API
export function getPermissionList() {
  return api.get('/admin/permissions')
}

export function grantUserPermission(account) {
  return api.post('/admin/permissions/grant', { account })
}

export function revokeUserPermission(userId) {
  return api.post('/admin/permissions/revoke', { user_id: userId })
}

export function searchUserByAccount(account) {
  return api.get('/admin/users/search', { params: { account } })
}

// Email 設定 API
export function getEmailConfig() {
  return api.get('/admin/email-config')
}

export function updateEmailConfig(config) {
  return api.post('/admin/email-config', config)
}

export function testEmailConfig() {
  return api.post('/admin/email-config/test')
}

// 為了相容性，保留舊的函數名稱
export const loginCsv = loginUser
export const registerCsv = registerUser